import { Validators } from '@angular/forms';
import { ConfigurationType, LmgmtKey } from 'apps/keba-wallbox-app/src/plugins/keba-wallbox-plugin';
import { FormInputType, SettingsFormData } from '../../components';
import { SettingsUnitsConverter, WallboxProductCodeRegex } from '../../utils';
import { SettingsValidators } from '../../utils/SettingsValidators';
import { WallboxConnectionType } from '../wallbox-connection-type.definition';

export const chargingNetworkFormDefinition: SettingsFormData[] = [
  {
    title: 'kwa.settings.charging-network.form-title',
    accessibilityId: 'charging-network-settings',
    elements: [
      {
        key: LmgmtKey.MAX_AVAILABLE_CURRENT,
        type: FormInputType.NUMBER,
        description: 'kwa.settings.charging-network.max-available-current.description',
        title: 'kwa.settings.charging-network.max-available-current.title',
        configurationType: ConfigurationType.LMGMT,
        format: '1.1-1',
        min: 6,
        max: 512,
        validators: [
          Validators.min(6),
          Validators.max(512),
          Validators.required,
          SettingsValidators.maxCurrentValidator,
        ],
        convertToDisplayUnit: SettingsUnitsConverter.milliAmpereToAmpere,
        convertFromDisplayUnit: SettingsUnitsConverter.ampereToMilliAmpere,
        accessibilityId: 'max-available-current',
        onChangeValidateFields: [LmgmtKey.MIN_DEFAULT_CURRENT, LmgmtKey.MAX_ASYM_PHASE_CURRENT],
      },
      {
        key: LmgmtKey.MIN_DEFAULT_CURRENT,
        type: FormInputType.NUMBER,
        description: 'kwa.settings.charging-network.min-charge-current.description',
        title: 'kwa.settings.charging-network.min-charge-current.title',
        configurationType: ConfigurationType.LMGMT,
        format: '1.1-1',
        min: 6,
        max: 32,
        validators: [
          Validators.required,
          Validators.min(6),
          Validators.max(32),
          SettingsValidators.maxCurrentValidator,
          SettingsValidators.compareToFieldValidator('max_available_current', (value: any, otherValue: any) => {
            let minDefaultCurrentValue: number = Number(value);
            let max = Number(otherValue);
            if (minDefaultCurrentValue >= max) {
              return {
                minDefaultCurrentInvalid: true,
              };
            }
            return null;
          }),
        ],
        convertToDisplayUnit: SettingsUnitsConverter.milliAmpereToAmpere,
        convertFromDisplayUnit: SettingsUnitsConverter.ampereToMilliAmpere,
        accessibilityId: 'min-default-current',
        onChangeValidateFields: [LmgmtKey.MAX_AVAILABLE_CURRENT],
      },
      {
        key: LmgmtKey.MAX_ASYM_PHASE_CURRENT,
        type: FormInputType.NUMBER,
        description: 'kwa.settings.charging-network.max-current-asymmetric-load.description',
        title: 'kwa.settings.charging-network.max-current-asymmetric-load.title',
        configurationType: ConfigurationType.LMGMT,
        format: '1.1-1',
        min: 0,
        max: 32,
        validators: [
          Validators.min(0),
          Validators.max(32),
          Validators.required,
          SettingsValidators.maxAsymPhaseCurrentValidator,
          SettingsValidators.compareToFieldValidator(LmgmtKey.TOR_ENABLED, (value: any, otherValue: any) => {
            if (otherValue === 'true' && value > 16) {
              return {
                max: {
                  max: 16,
                },
              };
            }
            return null;
          }),
        ],
        convertToDisplayUnit: SettingsUnitsConverter.milliAmpereToAmpere,
        convertFromDisplayUnit: SettingsUnitsConverter.ampereToMilliAmpere,
        accessibilityId: 'max-phase-current',
        hideConditions: {
          operator: 'or',
          conditions: [
            {
              operator: 'and',
              conditions: [
                { condition: 'productCode', value: WallboxProductCodeRegex.IS_P40 },
                { condition: 'connectionType', value: WallboxConnectionType.LOCAL_REST },
              ],
            },
            {
              operator: 'and',
              conditions: [
                { condition: 'productCode', value: WallboxProductCodeRegex.IS_P40 },
                { condition: 'connectionType', value: WallboxConnectionType.REMOTE },
              ],
            },
          ],
        },
      },
      {
        key: LmgmtKey.ENABLE_ASYM_NETWORK,
        type: FormInputType.TOGGLE,
        description: 'kwa.settings.charging-network.avoid-asymmetric-loads.description',
        title: 'kwa.settings.charging-network.avoid-asymmetric-loads.title',
        configurationType: ConfigurationType.LMGMT,
        accessibilityId: 'asym-network',
        lockOnValueRead: {
          configurationType: ConfigurationType.LMGMT,
          key: LmgmtKey.TOR_ENABLED,
          value: 'true',
          toggleServiceKey: ['kw.common.connectionTypes.isNotBle', 'kw.common.productTypes.isNotP40'],
        },
        hideConditions: {
          operator: 'or',
          conditions: [
            {
              operator: 'and',
              conditions: [
                { condition: 'productCode', value: WallboxProductCodeRegex.IS_P40 },
                { condition: 'connectionType', value: WallboxConnectionType.LOCAL_REST },
              ],
            },
            {
              operator: 'and',
              conditions: [
                { condition: 'productCode', value: WallboxProductCodeRegex.IS_P40 },
                { condition: 'connectionType', value: WallboxConnectionType.REMOTE },
              ],
            },
          ],
        },
        readOnlyInfoText: 'kw.settings.tor.locked-info',
      },
      {
        key: LmgmtKey.RANDOM_PROFILE_DELAY_ENABLE,
        configurationType: ConfigurationType.LMGMT,
        type: FormInputType.TOGGLE,
        description: 'kwa.settings.charging-network.random-profile-delay-enable.description',
        title: 'kwa.settings.charging-network.random-profile-delay-enable.title',
        accessibilityId: 'random-profile-delay-enable',
        onChangeSetValues: {
          ifValue: 'true',
          setValues: [
            {
              configurationType: ConfigurationType.LMGMT,
              configs: [{ key: LmgmtKey.RANDOM_PROFILE_MAX_DELAY, value: '600' }],
            },
          ],
        },
        toggleServiceKey: ['kw.common.firmwareVersions.atLeast.1-2-0', 'kw.common.patterns.isUKWallbox'],
        listenOnChanges: true,
      },
      {
        key: LmgmtKey.RANDOM_PROFILE_MAX_DELAY,
        configurationType: ConfigurationType.LMGMT,
        type: FormInputType.NUMBER,
        description: 'kwa.settings.charging-network.random-profile-max-delay.description',
        title: 'kwa.settings.charging-network.random-profile-max-delay.title',
        accessibilityId: 'random-profile-delay',
        min: 600,
        max: 1800,
        toggleServiceKey: ['kw.common.firmwareVersions.atLeast.1-2-0', 'kw.common.patterns.isUKWallbox'],
        showDependentOnOtherSetting: { [LmgmtKey.RANDOM_PROFILE_DELAY_ENABLE]: 'true' },
      },
    ],
  },
];
